import type { StylePreset } from '@gilding/shared';
import { STYLE_PRESETS, STYLE_PROFILES } from '@gilding/shared';
import { cn } from '../lib/classnames.js';
import { FoldedCorner } from './FoldedCorner.js';

interface StylePickerProps {
  value: StylePreset;
  onChange: (value: StylePreset) => void;
}

/**
 * Style preset picker. Each preset gets a bespoke 28px SVG glyph that hints
 * at the look — a lens iris for photoreal, a brush stroke for painterly, etc.
 * Active state is a folded-gold corner + a top hairline, no fill swap.
 */
export function StylePicker({ value, onChange }: StylePickerProps) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
      {STYLE_PRESETS.map((id) => {
        const p = STYLE_PROFILES[id];
        const active = id === value;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-pressed={active}
            className={cn(
              'group relative isolate flex flex-col gap-2 overflow-hidden rounded-sm px-4 py-4 text-left',
              'border bg-surface-elevated transition-colors duration-200 ease-page',
              active
                ? 'border-hairline-strong'
                : 'border-hairline hover:border-hairline-strong',
            )}
          >
            {/* Active state: thin gild rule across the top */}
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-x-3 top-0 h-px origin-center transition-transform duration-300 ease-page',
                active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-50',
              )}
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgb(var(--color-accent)) 50%, transparent)',
              }}
            />
            {/* Active state: folded gold corner at top-right */}
            {active && <FoldedCorner />}

            <div className="flex items-center justify-between gap-3">
              <StyleGlyph id={id} active={active} />
              <span
                aria-hidden="true"
                className={cn(
                  'font-display italic text-caption transition-colors',
                  // On touch the inactive label is visible at low contrast so
                  // the tile reads as a picker; on hover it returns to full
                  // ink-subtle. Active label is text-accent-deep.
                  active
                    ? 'text-accent-deep'
                    : 'text-ink-subtle opacity-70 md:opacity-0 md:group-hover:opacity-100',
                )}
              >
                {active ? 'picked' : 'tap to pick'}
              </span>
            </div>

            <div className="flex flex-col">
              <span className="heading-section text-[1.0625rem] leading-tight">{p.label}</span>
              <span className="mt-1 font-display italic text-caption text-ink-subtle">
                {p.oneLiner}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Bespoke per-preset glyph — a cohesive line-art set that names the look at a
 * glance: a camera (photoreal), a magazine cover (editorial), a manga shine
 * (anime), an isometric cube (3D), a paintbrush (painterly), an "Aa" type
 * specimen (logo/poster), and a lab flask (experimental). The whole glyph
 * lifts and grows a touch on hover (desktop) and tap (mobile, via the parent
 * `group` active state); a few add a bespoke flourish. All motion is
 * motion-safe, so reduced-motion users get a still glyph.
 */
function StyleGlyph({ id, active }: { id: StylePreset; active: boolean }) {
  const stroke = active ? 'rgb(var(--color-accent-deep))' : 'rgb(var(--color-ink-subtle))';
  const fill = active ? 'rgb(var(--color-accent))' : 'rgb(var(--color-ink-subtle))';
  const svgClass = cn(
    'h-7 w-7 shrink-0 origin-bottom transition-transform duration-300 ease-out',
    'motion-safe:group-hover:-translate-y-[2px] motion-safe:group-active:-translate-y-[2px]',
    'motion-safe:group-hover:scale-[1.1] motion-safe:group-active:scale-[1.1]',
  );
  const common = { viewBox: '0 0 28 28', 'aria-hidden': true as const };
  const line = {
    fill: 'none',
    stroke,
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (id) {
    case 'photoreal-cinematic':
      // Camera — body, viewfinder hump, lens, shutter dot.
      return (
        <svg {...common} className={svgClass}>
          <rect x="3" y="8.5" width="22" height="14.5" rx="2.5" {...line} />
          <path d="M9 8.5 L10.8 5.5 H17.2 L19 8.5" {...line} />
          <circle cx="14" cy="15.5" r="4.6" {...line} />
          <circle cx="14" cy="15.5" r="1.7" fill={fill} />
          <circle cx="20.5" cy="12" r="0.9" fill={fill} />
        </svg>
      );
    case 'editorial-illustration':
      // Magazine cover — portrait page, a bold hero image, headline lines.
      return (
        <svg {...common} className={svgClass}>
          <rect x="6.5" y="3.5" width="15" height="21" rx="2" {...line} />
          <circle cx="14" cy="11" r="4.2" fill={fill} />
          <path d="M9.5 18 H18.5 M9.5 21 H16" {...line} />
        </svg>
      );
    case 'anime-leaning':
      // Manga shine — a four-point star with a small twinkle that spins a
      // touch on hover.
      return (
        <svg
          {...common}
          className={cn(
            svgClass,
            'motion-safe:group-hover:rotate-[10deg] motion-safe:group-active:rotate-[10deg]',
          )}
        >
          <path
            d="M13 4 C 13.8 9.5 14.5 10.2 20 11 C 14.5 11.8 13.8 12.5 13 18 C 12.2 12.5 11.5 11.8 6 11 C 11.5 10.2 12.2 9.5 13 4 Z"
            fill={fill}
          />
          <path
            d="M21 16 c .4 2 .6 2.2 2.6 2.6 c -2 .4 -2.2 .6 -2.6 2.6 c -.4 -2 -.6 -2.2 -2.6 -2.6 c 2 -.4 2.2 -.6 2.6 -2.6 z"
            fill={fill}
          />
        </svg>
      );
    case '3d-render':
      // Isometric cube — top face lit, tilts on hover.
      return (
        <svg
          {...common}
          className={cn(
            svgClass,
            'motion-safe:group-hover:-rotate-[8deg] motion-safe:group-active:-rotate-[8deg]',
          )}
        >
          <path
            d="M14 3.5 L22.5 8 L14 12.5 L5.5 8 Z"
            fill={fill}
            fillOpacity="0.22"
            stroke={stroke}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path d="M5.5 8 L14 12.5 L14 22.5 L5.5 18 Z" {...line} />
          <path d="M22.5 8 L14 12.5 L14 22.5 L22.5 18 Z" {...line} />
        </svg>
      );
    case 'painterly':
      // Paintbrush — handle + loaded bristle, tilts like a stroke on hover.
      return (
        <svg
          {...common}
          className={cn(
            svgClass,
            'motion-safe:group-hover:-rotate-[8deg] motion-safe:group-active:-rotate-[8deg]',
          )}
        >
          <path d="M20 4.5 L23.5 8 L15 16.5 L11.5 13 Z" {...line} />
          <path
            d="M11.5 13 L15 16.5 C 13 18.5 8.5 19.5 5 19 C 6.5 16 9 14.5 11.5 13 Z"
            fill={fill}
          />
        </svg>
      );
    case 'logo-poster':
      // Type specimen — a serif "Aa", the type-forward tell.
      return (
        <svg {...common} className={svgClass}>
          <text
            x="14"
            y="20"
            textAnchor="middle"
            fontFamily="Fraunces, Georgia, serif"
            fontSize="18"
            fontWeight="600"
            fontStyle="italic"
            fill={stroke}
          >
            Aa
          </text>
        </svg>
      );
    case 'experimental':
      // Lab flask — bubbles drift up on hover.
      return (
        <svg {...common} className={svgClass}>
          <path d="M11 4 H17" {...line} />
          <path
            d="M12.7 4 V10.5 L7 20.5 A2 2 0 0 0 9 23.5 H19 A2 2 0 0 0 21 20.5 L15.3 10.5 V4"
            {...line}
          />
          <path d="M9.3 17.5 H18.7" fill="none" stroke={fill} strokeWidth="1.5" strokeLinecap="round" />
          <circle
            cx="12.5"
            cy="19.5"
            r="1"
            fill={fill}
            className="transition-transform duration-300 ease-out motion-safe:group-hover:-translate-y-[2px] motion-safe:group-active:-translate-y-[2px]"
          />
          <circle
            cx="15.5"
            cy="18.5"
            r="0.8"
            fill={fill}
            className="transition-transform duration-500 ease-out motion-safe:group-hover:-translate-y-[3px] motion-safe:group-active:-translate-y-[3px]"
          />
        </svg>
      );
  }
}
